import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { SHOWS_LABEL, type PublicProfile } from '../types';
import { Avatar } from '../components/Avatar';
import { BandRow } from '../components/BandRow';
import { PhotoGallery } from '../components/PhotoGallery';
import { ProfileActions } from '../components/ProfileActions';
import { VideoEmbed } from '../components/VideoEmbed';
import { videoEmbedUrl } from '../video';
import { PostCard } from '../components/PostCard';
import { Icon } from '../components/Icon';
import { useTiledBackground } from '../hooks/useTiledBackground';
import { formatCount } from '../format';
import type { Post } from '../types';

export function UserPage() {
  const { id } = useParams();
  const [u, setU] = useState<PublicProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [followBusy, setFollowBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!id) return;
    api.user(id).then(setU).catch((e) => setError(e.message));
    api.userPosts(id).then((r) => setPosts(r.posts)).catch(() => {});
  }, [id]);

  const toggleFollow = async () => {
    if (!u) return;
    setFollowBusy(true);
    try {
      if (u.followedByMe) { await api.unfollow(u.id); setU({ ...u, followedByMe: false, followers: Math.max(0, u.followers - 1) }); }
      else { await api.follow(u.id); setU({ ...u, followedByMe: true, followers: u.followers + 1 }); }
    } catch (e: any) { setError(e.message); } finally { setFollowBusy(false); }
  };

  // The tiled background covers the whole page while this profile is open.
  useTiledBackground(u?.backgroundUrl);

  const block = async () => {
    if (!id || !u || !confirm(`Block ${u.displayName}? They won't be able to message you and you won't see each other in results.`)) return;
    await api.block(id).catch((e) => setError(e.message));
    nav(-1);
  };

  if (error) return <div className="error">{error}</div>;
  if (!u) return <div className="spinner">Loading…</div>;

  const tops = u.bands.filter((b) => b.topRank !== null);
  const others = u.bands.filter((b) => b.topRank === null);
  const current = u.bandHistory.filter((e) => e.isCurrent);
  const past = u.bandHistory.filter((e) => !e.isCurrent);
  const videos = [...u.links.map((l) => ({ url: l.url, title: l.label })), ...u.bandHistory.filter((e) => e.url).map((e) => ({ url: e.url!, title: e.name }))]
    .filter((v) => videoEmbedUrl(v.url));
  const plainLinks = u.links.filter((l) => !videoEmbedUrl(l.url));

  return (
    <div className="stack">
      <div className="card row">
        <Avatar url={u.photoUrl} name={u.displayName} size="lg" />
        <div className="body">
          <div style={{ fontSize: 25, fontWeight: 700 }}>{u.displayName}{u.age !== null ? <span className="muted">, {u.age}</span> : null}</div>
          <div className="muted">{[u.city, u.showsPlayed ? SHOWS_LABEL[u.showsPlayed].toLowerCase() : null].filter(Boolean).join(' · ')}</div>
          <div style={{ marginTop: 6 }}>{u.instruments.map((i) => <span className="pill" key={i}>{i}</span>)}</div>
          <div className="row" style={{ marginTop: 8, gap: 12 }}>
            <span className="muted small"><strong>{formatCount(u.followers)}</strong> followers · <strong>{formatCount(u.following)}</strong> following{u.followsMe ? ' · follows you' : ''}</span>
          </div>
        </div>
      </div>
      <button className={`btn block ${u.followedByMe ? 'secondary' : ''}`} disabled={followBusy} onClick={toggleFollow}>{u.followedByMe ? <><Icon name="check" size={18} /> Following</> : <><Icon name="follow" size={20} /> Follow</>}</button>

      <ProfileActions u={u} onChange={(patch) => setU({ ...u, ...patch })} />

      <PhotoGallery photos={u.photos.length > 1 ? u.photos : []} name={u.displayName} />

      {u.bio && <div className="card">{u.bio}</div>}

      {current.length > 0 && (
        <div className="card">
          <h2>Current band{current.length > 1 ? 's' : ''}</h2>
          {current.map((e, i) => <HistoryRow key={e.id ?? i} e={e} />)}
        </div>
      )}

      {(u.wants.instruments.length > 0 || u.wants.genres.length > 0) && (
        <div className="card">
          <h2>Looking for</h2>
          <div className="chips">
            {u.wants.instruments.map((w) => <span key={`i-${w}`} className="chip static">🎵 {w}</span>)}
            {u.wants.genres.map((w) => <span key={`g-${w}`} className="chip static">{w}</span>)}
          </div>
        </div>
      )}

      {u.genres.length > 0 && (
        <div className="card"><h2>Genres</h2><div className="chips">{u.genres.map((g) => <span key={g} className="chip static">{g}</span>)}</div></div>
      )}
      {tops.length > 0 && (
        <div className="card"><h2>Top bands</h2><div className="band-list">{tops.map((b) => <BandRow key={b.mbid} band={b} rank={b.topRank} />)}</div></div>
      )}
      {others.length > 0 && (
        <div className="card"><h2>Also likes</h2><div className="band-list">{others.map((b) => <BandRow key={b.mbid} band={b} />)}</div></div>
      )}

      {videos.length > 0 && (
        <div className="card stack"><h2>Videos</h2>{videos.map((v, i) => <VideoEmbed key={i} url={v.url} title={v.title} />)}</div>
      )}

      {past.length > 0 && (
        <div className="card">
          <h2>Past bands</h2>
          {past.map((e, i) => <HistoryRow key={e.id ?? i} e={e} />)}
        </div>
      )}
      {plainLinks.length > 0 && (
        <div className="card">
          <h2>Links</h2>
          <div className="chips">{plainLinks.map((l, i) => <a key={l.id ?? i} className="chip" href={l.url} target="_blank" rel="noreferrer">{l.label} ↗</a>)}</div>
        </div>
      )}
      {posts.length > 0 && (
        <div className="stack"><h2 className="section">Posts</h2>{posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
      )}
      <button className="btn danger sm" onClick={block} style={{ alignSelf: 'center' }}>Block {u.displayName}</button>
    </div>
  );
}

function HistoryRow({ e }: { e: PublicProfile['bandHistory'][number] }) {
  return (
    <div className="list-item">
      {e.imageUrl && <img src={e.imageUrl} alt="" className="band-thumb" />}
      <div className="body">
        <strong>{e.url && !videoEmbedUrl(e.url) ? <a href={e.url} target="_blank" rel="noreferrer">{e.name} ↗</a> : e.name}</strong>
        {e.role && <div className="muted small">{e.role}</div>}
      </div>
    </div>
  );
}
